import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import Button from '@/components/ui/Button';
import { useGetAuthApi, usePostAuthApi } from '@/hooks/UseApiCalls';
import { CreditStandingOrderDetailsDtoIn } from '@/models/StandingOrderDetails';
import NedarimStandingOrderDtoIn from '@/models/NedarimStandingOrderDtoIn';
import {
  CURRENCY_BY_CODE,
  UNLIMITED_PAYMENTS,
  formatDate,
  formatIsoDate,
  formatMoney,
  formatRowMoney,
  getCurrencyCode,
  getPaymentSourceLabel,
  isUnlimited,
  parseDate,
} from '../standingOrderModel';
import {
  EditForm,
  StandingOrderActionResult,
  UpdateCreditStandingOrderRequest,
  actionUrl,
  describeActionError,
  detailsUrl,
  hasChanges,
  hasErrors,
  initialEditForm,
  isSharedMosad,
  planEdit,
  withLiveTerms,
} from '../standingOrderActions';
import chargeStyles from '../AdditionalInfo/StandingOrderChargeDialog.module.css';
import styles from './OrderActions.module.css';

interface EditOrderDialogProps {
  order: NedarimStandingOrderDtoIn;
  fundId: number;
  onClose: () => void;
  onDone: (kind: 'edit', result: StandingOrderActionResult, request: UpdateCreditStandingOrderRequest) => void;
}

const CURRENCY_SYMBOL: Record<1 | 2, string> = { 1: '₪', 2: '$' };

/** Tomorrow, local, as the date input wants it. */
function tomorrowIso(): string {
  const date = new Date();
  date.setDate(date.getDate() + 1);
  return formatIsoDate(date);
}

/**
 * Editing an order's terms: the amount, the next charge date, the payments left and the note.
 * Not the donor and not the card - the donor's details are what the receipt is issued against and
 * stay with the office, and the card is replaced by the donor through the link in the blade.
 *
 * Only what was changed is sent, so a value somebody changed meanwhile in Nedarim's own interface
 * is not overwritten with the one this form opened on. The amount may only go down (decided
 * 4.10.2026); the note of an order on a shared mosad is locked, because the fund's number in it is
 * what ties the order to this fund. The server holds the same rules.
 */
const EditOrderForm = ({ order, fundId, onClose, onDone }: EditOrderDialogProps) => {
  const { t, i18n } = useTranslation();
  const prefix = 'standingOrders.orderActions.editDialog';
  const [form, setForm] = useState<EditForm>(() => initialEditForm(order));
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const plan = useMemo(() => planEdit(order, form), [order, form]);
  const locked = isSharedMosad(order);

  const currencyCode = getCurrencyCode(order.currency);
  const currencyIso = currencyCode == null ? 'ILS' : CURRENCY_BY_CODE[currencyCode];
  const money = (value: number) => formatMoney(value, currencyIso, i18n.language);
  const day = (date: Date) => formatDate(date, i18n.language);

  const { mutate, isPending } = usePostAuthApi<StandingOrderActionResult, UpdateCreditStandingOrderRequest>(
    actionUrl(fundId, order, 'edit'),
    {
      onSuccess: (result, request) => {
        if (!result.success) {
          setError(t('standingOrders.orderActions.errors.nedarimRefused', { message: result.message ?? '' }));
          return;
        }
        setSaved(true);
        onDone('edit', result, request);
      },
      onError: (e) => setError(describeActionError(e, t)),
    },
  );

  const update = (patch: Partial<EditForm>) => {
    setError(null);
    setForm((current) => ({ ...current, ...patch }));
  };

  const name = order.clientName || t('standingOrders.donor');
  const source = getPaymentSourceLabel(order);
  const currentNext = parseDate(order.nextDate);
  const remaining = order.remainingPayments;
  const paymentsNow = remaining == null || isUnlimited(remaining)
    ? t('standingOrders.unlimited')
    : remaining > 0
      ? t(`${prefix}.paymentsLeft`, { count: remaining })
      : t(`${prefix}.noPaymentsLeft`);

  // What saving will do, a sentence per change - the amount is the one a coordinator must not
  // get wrong, so it says from what, to what, and from when.
  const summary: string[] = [];
  if (plan.changes.amount) {
    const from = plan.changes.nextDate?.to ?? currentNext;
    summary.push(from
      ? t(`${prefix}.summaryAmount`, { from: money(plan.changes.amount.from), to: money(plan.changes.amount.to), date: day(from) })
      : t(`${prefix}.summaryAmountNoDate`, { from: money(plan.changes.amount.from), to: money(plan.changes.amount.to) }));
  }
  if (plan.changes.nextDate) summary.push(t(`${prefix}.summaryNextDate`, { date: day(plan.changes.nextDate.to) }));
  if (plan.changes.payments) {
    summary.push(plan.changes.payments.to >= UNLIMITED_PAYMENTS
      ? t(`${prefix}.summaryUnlimited`)
      : t(`${prefix}.summaryPayments`, { count: plan.changes.payments.to }));
  }
  if (plan.changes.comments) {
    summary.push(form.comments.trim() ? t(`${prefix}.summaryComments`) : t(`${prefix}.summaryCommentsCleared`));
  }

  const fieldError = (field: keyof typeof plan.errors) =>
    plan.errors[field] ? <span className={styles.fieldError}>{t(`${prefix}.errors.${plan.errors[field]}`)}</span> : null;

  const canSave = hasChanges(plan) && !hasErrors(plan) && !isPending && currencyCode != null;

  return (
    <MainDialogLayout
      open
      onOpenChange={(open: boolean) => { if (!open && !isPending) onClose(); }}
      title={t(`${prefix}.title`, { name })}
      maxWidth="680px"
      fitContent
      contentClassName={chargeStyles.compactDialog}
    >
      <div className={chargeStyles.container}>
        <div className={chargeStyles.form}>
          <div className={chargeStyles.context}>
            <span>{[name, source && t('standingOrders.orderActions.cancelDialog.card', { source })].filter(Boolean).join(' · ')}</span>
            <b>
              {currentNext
                ? t(`${prefix}.current`, { amount: formatRowMoney(order.amount, order, i18n.language), payments: paymentsNow, date: day(currentNext) })
                : t(`${prefix}.currentNoDate`, { amount: formatRowMoney(order.amount, order, i18n.language), payments: paymentsNow })}
            </b>
          </div>

          <div className={chargeStyles.fieldRow}>
            <div className={chargeStyles.field}>
              <label className={chargeStyles.label} htmlFor="edit-order-amount">
                {t(`${prefix}.amount`)}<span className={chargeStyles.required}>*</span>
              </label>
              <div className={chargeStyles.inputWrapper}>
                <span className={chargeStyles.currencyBadge}>{currencyCode == null ? '' : CURRENCY_SYMBOL[currencyCode]}</span>
                <input
                  id="edit-order-amount"
                  className={chargeStyles.input}
                  type="number"
                  min="1"
                  max={order.amount}
                  step="any"
                  value={form.amount}
                  onChange={(e) => update({ amount: e.target.value })}
                  onWheel={(e) => (e.target as HTMLInputElement).blur()}
                  disabled={isPending || saved}
                  aria-invalid={Boolean(plan.errors.amount)}
                />
              </div>
              {fieldError('amount') ?? <span className={styles.hint}>{t(`${prefix}.amountHint`)}</span>}
            </div>

            <div className={chargeStyles.field}>
              <label className={chargeStyles.label} htmlFor="edit-order-next-date">{t(`${prefix}.nextDate`)}</label>
              <input
                id="edit-order-next-date"
                className={chargeStyles.input}
                type="date"
                min={tomorrowIso()}
                value={form.nextDate}
                onChange={(e) => update({ nextDate: e.target.value })}
                disabled={isPending || saved}
                aria-invalid={Boolean(plan.errors.nextDate)}
              />
              {fieldError('nextDate') ?? <span className={styles.hint}>{t(`${prefix}.nextDateHint`)}</span>}
            </div>
          </div>

          <div className={chargeStyles.fieldRow}>
            <div className={chargeStyles.field}>
              <label className={chargeStyles.label} htmlFor="edit-order-payments">{t(`${prefix}.payments`)}</label>
              <input
                id="edit-order-payments"
                className={`${chargeStyles.input} ${form.unlimited ? styles.inputLocked : ''}`}
                type="number"
                min="1"
                step="1"
                value={form.unlimited ? '' : form.remainingPayments}
                placeholder={form.unlimited ? t('standingOrders.unlimited') : ''}
                onChange={(e) => update({ remainingPayments: e.target.value })}
                onWheel={(e) => (e.target as HTMLInputElement).blur()}
                disabled={form.unlimited || isPending || saved}
                aria-invalid={Boolean(plan.errors.payments)}
              />
              <label className={styles.unlimited} htmlFor="edit-order-unlimited">
                <input
                  id="edit-order-unlimited"
                  type="checkbox"
                  checked={form.unlimited}
                  onChange={(e) => update({ unlimited: e.target.checked })}
                  disabled={isPending || saved}
                />
                {t('standingOrders.unlimited')}
              </label>
              {fieldError('payments')}
            </div>

            <div className={chargeStyles.field}>
              <label className={chargeStyles.label} htmlFor="edit-order-comments">{t(`${prefix}.comments`)}</label>
              <input
                id="edit-order-comments"
                className={`${chargeStyles.input} ${locked ? styles.inputLocked : ''}`}
                type="text"
                value={form.comments}
                onChange={(e) => update({ comments: e.target.value })}
                disabled={locked || isPending || saved}
                maxLength={300}
                aria-invalid={Boolean(plan.errors.comments)}
              />
              {fieldError('comments') ?? (locked && <span className={styles.hint}>{t(`${prefix}.commentsLocked`)}</span>)}
            </div>
          </div>

          {summary.length > 0 && !saved && (
            <div className={styles.summary} aria-live="polite">
              {summary.map((line) => <span key={line}>{line}</span>)}
            </div>
          )}

          {error && <div role="alert" className={chargeStyles.errorMessage}>{error}</div>}
          {saved && <div role="status" className={chargeStyles.successMessage}>{t(`${prefix}.saved`)}</div>}

          <div className={chargeStyles.actions}>
            {saved ? (
              <Button variant="primary" onClick={onClose}>{t(`${prefix}.close`)}</Button>
            ) : (
              <>
                <Button variant="primary" disabled={!canSave} onClick={() => { setError(null); mutate(plan.request); }}>
                  {isPending ? t(`${prefix}.saving`) : t(`${prefix}.save`)}
                </Button>
                <Button
                  variant="outline"
                  className="!h-[52px] !min-w-[120px] !px-6"
                  onClick={onClose}
                  disabled={isPending}
                >
                  {t(`${prefix}.cancel`)}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </MainDialogLayout>
  );
};

/**
 * The form opens on the order as Nedarim holds it now (HAT-1382). The board's list is cached
 * for up to five minutes, so an amount restored in Nedarim's own interface is not in it yet, and
 * a form that opened on the listed amount would also cap the field at it. The live read is the
 * one the blade makes; if it fails the form opens on the list, and the server judges the edit on
 * the live order either way.
 */
const EditOrderDialog = (props: EditOrderDialogProps) => {
  const { t } = useTranslation();
  const { order, fundId, onClose } = props;
  const { data: live, isFetching } = useGetAuthApi<CreditStandingOrderDetailsDtoIn>(
    detailsUrl(fundId, order), undefined, { retry: false },
  );

  const current = useMemo(() => withLiveTerms(order, live), [order, live]);

  // The first read only: a refetch after the save must not swap the form for the spinner.
  const [ready, setReady] = useState(false);
  useEffect(() => { if (!isFetching) setReady(true); }, [isFetching]);

  if (!ready) {
    return (
      <MainDialogLayout open onOpenChange={(open: boolean) => { if (!open) onClose(); }} maxWidth="680px" fitContent title={t('loading')}>
        <div className={chargeStyles.container}>{t('loading')}</div>
      </MainDialogLayout>
    );
  }

  return <EditOrderForm {...props} order={current} />;
};

export default EditOrderDialog;
